import { useMemo, useEffect } from "react";
import { Button } from "@/components/kit";

// One-time rose-petal celebration shown on the Join Wizard confirmation screen.
// Non-blocking: petals never capture clicks; the member can continue immediately.
export default function PetalCelebration({ message, onContinue }) {
  // Soft chime on appear (Web Audio — no asset needed). Best-effort; ignored if blocked.
  useEffect(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6 — gentle arpeggio
      notes.forEach((f, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = f;
        const t = now + i * 0.12;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.16, t + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
        o.connect(g);
        g.connect(ctx.destination);
        o.start(t);
        o.stop(t + 1.0);
      });
      const done = setTimeout(() => { try { ctx.close(); } catch (e) {} }, 1600);
      return () => clearTimeout(done);
    } catch (e) { /* audio not available */ }
  }, []);

  // Falling petals (drift down the whole screen).
  const petals = useMemo(
    () =>
      Array.from({ length: 22 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 2.5,
        dur: 4 + Math.random() * 3,
        size: 12 + Math.random() * 16,
        rot: Math.random() * 360,
        drift: (Math.random() - 0.5) * 120,
        hue: ["#b91c4b", "#d6446b", "#e06a8a", "#9d1c3f", "#c8365c"][i % 5],
      })),
    []
  );
  // Confetti-of-petals burst radiating from the welcome card.
  const burst = useMemo(
    () =>
      Array.from({ length: 26 }).map((_, i) => {
        const ang = (i / 26) * Math.PI * 2 + Math.random() * 0.3;
        const dist = 120 + Math.random() * 200;
        return {
          id: i,
          dx: Math.cos(ang) * dist,
          dy: Math.sin(ang) * dist - 40,
          size: 8 + Math.random() * 12,
          rot: Math.random() * 540,
          delay: Math.random() * 0.15,
          hue: ["#b91c4b", "#d6446b", "#e06a8a", "#9d1c3f", "#c8365c", "#f0a5ba"][i % 6],
        };
      }),
    []
  );
  return (
    <div className="fixed inset-0 z-[100] pointer-events-none overflow-hidden" data-testid="petal-celebration">
      <style>{`
        @keyframes rca-petal-fall {
          0%   { transform: translateY(-12vh) translateX(0) rotate(0deg); opacity: 0; }
          10%  { opacity: 1; }
          100% { transform: translateY(112vh) translateX(var(--drift)) rotate(var(--rot)); opacity: 1; }
        }
        @keyframes rca-welcome-pop {
          0%   { transform: scale(0.8) translateY(10px); opacity: 0; }
          60%  { transform: scale(1.04) translateY(0); opacity: 1; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes rca-petal-burst {
          0%   { transform: translate(0,0) rotate(0deg) scale(0.4); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translate(var(--dx), var(--dy)) rotate(var(--br)) scale(1); opacity: 0; }
        }
      `}</style>
      {petals.map((p) => (
        <div
          key={p.id}
          style={{
            position: "absolute",
            top: 0,
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 0.72,
            background: p.hue,
            borderRadius: "0 100% 0 100%",
            "--drift": `${p.drift}px`,
            "--rot": `${p.rot + 360}deg`,
            animation: `rca-petal-fall ${p.dur}s cubic-bezier(.4,.05,.6,1) ${p.delay}s forwards`,
            boxShadow: "inset 0 -2px 4px rgba(0,0,0,0.12)",
          }}
        />
      ))}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
        <div className="relative">
          {burst.map((b) => (
            <div
              key={b.id}
              data-testid="petal-burst"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: b.size,
                height: b.size * 0.72,
                marginLeft: -b.size / 2,
                background: b.hue,
                borderRadius: "0 100% 0 100%",
                "--dx": `${b.dx}px`,
                "--dy": `${b.dy}px`,
                "--br": `${b.rot}deg`,
                animation: `rca-petal-burst 1.1s cubic-bezier(.15,.6,.4,1) ${b.delay}s forwards`,
              }}
            />
          ))}
          <div
            className="pointer-events-auto bg-cream/95 backdrop-blur border border-maroon/20 rounded-2xl shadow-2xl px-8 py-7 text-center max-w-md"
            style={{ animation: "rca-welcome-pop 0.6s ease-out both" }}
            data-testid="petal-welcome-card"
          >
            <div className="text-4xl mb-2">🌹</div>
            <h2 className="font-zilla text-3xl font-semibold text-maroon leading-tight" data-testid="petal-welcome-message">
              {message}
            </h2>
            <Button data-testid="petal-continue" onClick={onContinue} className="mt-5 px-6 py-3 min-h-[48px]">
              Go to my dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
